/*
 * The CustomerConfirmBudgetStep component is the step in customer SO authorization flow where wallet
 * settings are viewed and confirmed.
 * 
 * This takes the following props:
 *   auth                 - authentication object
 *   account              - customer for which this SO is being created
 *   project              - project for which this SO is being created
 *   assignedStep
 *   activeStep
 *   handleBack
 *   handleNext
 */

import React from 'react';
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';
import { Button, Grid, Paper, Typography } from '@mui/material';
import AppBindStyles from '../../AppBindStyles';

const styleSheet = theme => ({
  root: {
    flexGrow: 1,
  },
  accountLogo: {
    float: 'left',
    marginRight: 15
  },
  button: {
    width: 52,
  },
  input: {
    display: 'none',
  },
  currencyWidth: {
    midWidth: 80,
    width: 80,
    textAlign: 'right'
  },
  tipPaper: {
    padding: 16,
  }
});

class CustomerConfirmBudgetStep extends React.Component {
  
  isBackDisabled() {
    if (this.props.activeStep === 0)
      return true;

    return false;
  }

  isNextDisabled(queryProps) {
    if (this.props.activeStep === this.props.assignedStep) {
      return false;
    }
    return false;
  }

  render() {
    const classes = this.props.classes;

    let theAccount         = this.props.account;
    let theProject         = this.props.project;
    let theCustomerWallet  = null;

    let budgetApprovedCount  = theAccount.budgetApprovedCount;
    let initialBalanceAmount = theAccount.initialBalanceAmount;
    let rAutoTopupAmount     = theAccount.rAutoTopupAmount;
    let rAutoTopupThreshold  = theAccount.rAutoTopupThreshold;
    let rMaxMonthlyBudget    = theAccount.rMaxMonthlyBudget;

    if (theProject != null) {
      theCustomerWallet    = theProject.walletTByCustomerWalletId;
      initialBalanceAmount = theCustomerWallet.initialBalanceAmount;
      rAutoTopupAmount     = theCustomerWallet.rAutoTopupAmount;
      rAutoTopupThreshold  = theCustomerWallet.rAutoTopupThreshold;
      rMaxMonthlyBudget    = theCustomerWallet.rMaxMonthlyBudget;
    }

    let initialBalanceAmountText = '';
    if (initialBalanceAmount != null) {
      initialBalanceAmountText = Number(initialBalanceAmount).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
    }
    let rAutoTopupAmountText     = Number(rAutoTopupAmount).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2}); 
    let rAutoTopupThresholdText  = Number(rAutoTopupThreshold).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2}); 
    let rMaxMonthlyBudgetText = 'No limit';
    if (rMaxMonthlyBudget !== null) {
      rMaxMonthlyBudgetText = Number(rMaxMonthlyBudget).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2}); 
    }

    return (
      <Grid container spacing={2} >
        {theAccount.useBudgets >= 1 &&
        <React.Fragment>
          <Grid item xs={12}>
            <Grid container spacing={0} direction="row" justifyContent="space-between" >
              <Grid item xs={12}>
                <Typography variant="h6">Your expense wallet</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant='body2'>We need your authorization to set up and fund an expense wallet to pay the subscription costs for your project.</Typography>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Grid container spacing={1}>
              {budgetApprovedCount === 0 &&
              <Grid item xs={12}>
                <Grid container spacing={2} direction="row" justifyContent="space-between" >
                  <Grid item>
                    <Typography variant='body1'>Initially fund your wallet to</Typography>
                  </Grid>
                  <Grid item className={classes.currencyWidth}>
                    <Typography variant='body1'>{initialBalanceAmountText}</Typography>
                  </Grid>
                </Grid>
              </Grid>}
              {budgetApprovedCount > 0 && initialBalanceAmount !== null && initialBalanceAmount > 0 &&
              <Grid item xs={12}>
                <Grid container spacing={2} direction="row" justifyContent="space-between" >
                  <Grid item>
                    <Typography variant='body1'>Add funds to your wallet</Typography>
                  </Grid>
                  <Grid item className={classes.currencyWidth}>
                    <Typography variant='body1'>{initialBalanceAmountText}</Typography>
                  </Grid>
                </Grid>
              </Grid>}

              {Number(rAutoTopupThreshold) > 0 &&
              <Grid item xs={12}>
                <Grid container spacing={2} direction="row" justifyContent="space-between" >
                  <Grid item>
                    <Typography variant='body1'>When your wallet balance falls below</Typography>
                  </Grid>
                  <Grid item className={classes.currencyWidth}>
                    <Typography variant='body1'>{rAutoTopupThresholdText}</Typography>
                  </Grid>
                </Grid>
              </Grid>}
              {Number(rAutoTopupAmount) > 0 &&
              <Grid item xs={12}>
                <Grid container spacing={2} direction="row" justifyContent="space-between" >
                  <Grid item>
                  <Typography variant='body1'>Automatically add funds to get your wallet balance to</Typography>
                  </Grid>
                  <Grid item className={classes.currencyWidth}>
                    <Typography variant='body1'>{rAutoTopupAmountText}</Typography>
                  </Grid>
                </Grid>
              </Grid>}

              <Grid item xs={12}>
                <Grid container spacing={2} direction="row" justifyContent="space-between" >
                  <Grid item>
                    <Typography variant='body1'>Limit monthly spend to</Typography>
                  </Grid>
                  <Grid item className={classes.currencyWidth}>
                    {rMaxMonthlyBudgetText === 'No limit' &&
                    <Typography variant='body1'><i>{rMaxMonthlyBudgetText}&nbsp;</i></Typography>}
                    {rMaxMonthlyBudgetText !== 'No limit' &&
                    <Typography variant='body1'>{rMaxMonthlyBudgetText}</Typography>}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          
          <Grid item xs={12}>
            <Paper elevation={1} className={classes.tipPaper}>
              <Grid container spacing={1} direction="row" justifyContent="flex-start">
                <Grid item xs={12}>
                  <div style={{display:"flex"}}>
                    <Typography variant="body1">
                      <strong>Tip.</strong>&nbsp;&nbsp;You can refund the balance in your wallet at any time.
                    </Typography>
                  </div>
                </Grid>      
              </Grid>
            </Paper>
          </Grid>
        </React.Fragment>}

        {this.props.activeStep === this.props.assignedStep &&
        <Grid item xs={12}>
          { (this.props.activeStep > 0) &&
          <Button
            disabled={this.isBackDisabled()}
            color="primary" variant="outlined"
            onClick={() => this.props.handleBack()}
            style={AppBindStyles.bottomButton}>
            Back
          </Button>}

          <Button
            color="primary" variant="contained"
            disabled={this.isNextDisabled()}
            onClick={() => this.props.handleNext()}
            style={AppBindStyles.bottomButton}>
              Confirm
          </Button>
        </Grid>} 
      </Grid>       
    );
  } 
}

CustomerConfirmBudgetStep.propTypes = {
  assignedStep: PropTypes.number.isRequired,
  activeStep: PropTypes.number.isRequired,
  handleBack: PropTypes.func.isRequired,
  handleNext: PropTypes.func.isRequired
};

export default withStyles(styleSheet)(CustomerConfirmBudgetStep);
